import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as uuid from 'uuid';
import { SpecialUser } from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { tokenDiffRenderer } from '@src/Components/PendingChanges/pending-changes-utils';
import { ExperimentTable } from './ExperimentTable';
import { GroupedDataRow } from './types';
import { EMPTY_CELL_TEXT, MAX_OVERLAY_LENGTH, MAX_SHOWN_GROUPED_USER_EMAILS } from './utils';
import './ExperimentTable.scss';

const userColumnRenderer = (data: GroupedDataRow<SpecialUser, 'user'>) => {
  if (!data.user) {
    return <div className='experiment-cell-text'>{EMPTY_CELL_TEXT}</div>;
  }

  if (data.user.length <= MAX_SHOWN_GROUPED_USER_EMAILS) {
    return <div className='experiment-cell-text'>{data.user.join(', ')}</div>;
  }

  const sortedUserEmails = [...data.user].sort();
  const shownUserEmails = sortedUserEmails.slice(0, MAX_SHOWN_GROUPED_USER_EMAILS);
  const remainingCount = sortedUserEmails.length - shownUserEmails.length;

  const overlayShownUserEmails = sortedUserEmails.slice(0, MAX_OVERLAY_LENGTH);
  const overlayRemainingCount = sortedUserEmails.length - overlayShownUserEmails.length;

  return (
    <OverlayTrigger
      placement='left'
      overlay={
        <Tooltip id={uuid.v4()} className='experiment-overlay-trigger'>
          <div className='tooltip-text-container'>
            {overlayShownUserEmails.map(userEmail => (
              <div className='tooltip-item' key={userEmail}>
                {userEmail}
              </div>
            ))}
            {overlayRemainingCount > 0 && (
              <div className='tooltip-item'>{`+ ${overlayRemainingCount} more`}</div>
            )}
          </div>
        </Tooltip>
      }
    >
      <div className='experiment-cell-text'>
        {`${shownUserEmails.join(', ')}${remainingCount > 0 ? ` + ${remainingCount} more` : ''}`}
      </div>
    </OverlayTrigger>
  );
};

const headers = [
  {
    label: 'User Group',
    field: 'user_group',
    type: 'text',
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (userGroup: string) => !userGroup || userGroup.length === 0
  },
  {
    label: 'User Emails',
    field: 'user',
    type: 'select',
    isMulti: true,
    options: 'special_user',
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (user: string) => !user || user.length === 0,
    renderer: userColumnRenderer,
    diffRenderer: tokenDiffRenderer<GroupedDataRow<SpecialUser, 'user'>>('user')
  }
];

const USER_GROUPS_EXPECTED_KEYS: (keyof SpecialUser & string)[] = ['user_group', 'user'];

const createNewSpecialUser = (groupByColumn: string, groupedColumn: string): SpecialUser => ({
  user: groupedColumn,
  user_group: groupByColumn
});

export const UserGroups = () => (
  <ExperimentTable<SpecialUser>
    options={{
      headers,
      name: 'User Groups',
      addButtonText: 'Add Group',
      createNewRow: createNewSpecialUser,
      groupByColumn: 'user_group',
      groupedColumn: 'user',
      expectedKeys: USER_GROUPS_EXPECTED_KEYS,
      jsonField: 'special_users',
      modalTitle: 'New User Group'
    }}
  />
);
